import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Snackbar,
  Pagination,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase-config";
import AddCategoryModal from "../components/add-category-modal";
import AddProductModal from "../components/add-product-modal";
import Collections from "../collections";
import Header from "../components/header";
import EditItemModal from "../components/EditItemModal";

const Inventory = () => {
  const theme = useTheme();
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [updatedValues, setUpdatedValues] = useState({});
  const [loadingField, setLoadingField] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [initialLoading, setInitialLoading] = useState(false);
  const [totals, setTotals] = useState({
    totalQuantity: 0,
    totalSold: 0,
    totalPrice: 0,
    totalUnitPrice: 0,
  });
  const [categories, setCategories] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "table"
  const [currentPage, setCurrentPage] = useState(1);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const itemsPerPage = 12;
  const LOW_STOCK_THRESHOLD = 10;

  const getStockStatus = (quantity) => {
    if (quantity === 0) return "out";
    if (quantity <= LOW_STOCK_THRESHOLD) return "low";
    return "ok";
  };



  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const categorySnapshot = await getDocs(
        collection(db, Collections.INVENTORY_CATEGORY)
      );
      const categoriesData = categorySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching categories: ", error);
    }
  }, []);

  // Calculate totals
  const calculateTotals = useCallback((items) => {
    let totalQuantity = 0;
    let totalSold = 0;
    let totalPrice = 0;
    let totalUnitPrice = 0;

    items.forEach((product) => {
      const availableQuantity = Number(product.availableQuantity) || 0;
      const soldQuantity = Number(product.soldQuantity) || 0;
      const price = Number(product.price) || 0;

      totalQuantity += availableQuantity;
      totalSold += soldQuantity;
      totalUnitPrice += price;
      totalPrice += availableQuantity * price;
    });

    setTotals({ totalQuantity, totalSold, totalPrice, totalUnitPrice });
  }, []);

  // Initial data fetch
  useEffect(() => {
    setInitialLoading(true);
    fetchCategories();

    const unsubscribe = onSnapshot(
      collection(db, Collections.INVENTORY_ITEMS),
      (snapshot) => {
        const updatedItems = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setProducts(updatedItems);
        calculateTotals(updatedItems);
        setInitialLoading(false);
      },
      (error) => {
        console.error("Error fetching data: ", error);
        setInitialLoading(false);
      }
    );

    return () => unsubscribe();
  }, [fetchCategories, calculateTotals]);

  // Delete item
  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      const inventoryQuery = query(
        collection(db, Collections.INVENTORY_ITEMS),
        where("id", "==", itemToDelete.id)
      );
      const querySnapshot = await getDocs(inventoryQuery);

      if (querySnapshot.empty) {
        setSnackbarMessage("Item not found!");
        setSnackbarOpen(true);
        return;
      }

      const docId = querySnapshot.docs[0].id;
      const itemRef = doc(db, Collections.INVENTORY_ITEMS, docId);
      await deleteDoc(itemRef);

      setProducts((prevProducts) =>
        prevProducts.filter((product) => product.id !== itemToDelete.id)
      );

      setSnackbarMessage("Item deleted successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting document: ", error);
      setSnackbarMessage("Error deleting item.");
      setSnackbarOpen(true);
    } finally {
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  // Update field
  const handleUpdate = async (itemId, field) => {
    const newValue = updatedValues[itemId]?.[field];
    if (newValue === undefined) return;

    try {
      const inventoryQuery = query(
        collection(db, Collections.INVENTORY_ITEMS),
        where("id", "==", itemId)
      );
      const querySnapshot = await getDocs(inventoryQuery);

      if (querySnapshot.empty) {
        console.error("No document found with the provided UUID");
        return;
      }

      const docId = querySnapshot.docs[0].id;
      const itemRef = doc(db, Collections.INVENTORY_ITEMS, docId);

      setLoadingField(field);

      await updateDoc(itemRef, {
        [field]: String(newValue),
        updatedAt: new Date(),
      });

      setSnackbarMessage(`${field} updated successfully!`);
      setSnackbarOpen(true);
      setEditingItemId(null);
      setEditingField(null);
    } catch (err) {
      console.error("Error updating item: ", err);
      setSnackbarMessage("Error updating item.");
      setSnackbarOpen(true);
    } finally {
      setLoadingField(null);
    }
  };

  // Filter products
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.title
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());

    const categoryName = categories.find(cat => cat.id === product.categoryId)?.category || '';
    const matchesCategorySearch = categoryName
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesCategoryFilter = categoryFilter
      ? product.categoryId === categoryFilter
      : true;

    return (matchesSearch || matchesCategorySearch) && matchesCategoryFilter;
  });

  // Pagination
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get category name by ID
  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.category : "Uncategorized";
  };

  const handleSaveEdit = async (updatedItem) => {
    if (!selectedItem) return;

    setSaveLoading(true);
    try {
      // Find the document by ID
      const inventoryQuery = query(
        collection(db, Collections.INVENTORY_ITEMS),
        where("id", "==", selectedItem.id)
      );
      const querySnapshot = await getDocs(inventoryQuery);

      if (querySnapshot.empty) {
        setSnackbarMessage("Item not found!");
        setSnackbarOpen(true);
        return;
      }

      const docId = querySnapshot.docs[0].id;
      const itemRef = doc(db, Collections.INVENTORY_ITEMS, docId);

      // Prepare update data - include ALL fields from the form
      const updateData = {
        title: updatedItem.title || "",
        brand: updatedItem.brand || "",
        vendor: updatedItem.vendor || "",
        categoryId: updatedItem.categoryId || "",
        units: updatedItem.units || "",
        availableQuantity: Number(updatedItem.availableQuantity) || 0,
        price: Number(updatedItem.price) || 0,
        soldQuantity: Number(updatedItem.soldQuantity) || 0,
        updatedAt: new Date(),
      };

      await updateDoc(itemRef, updateData);

      setSnackbarMessage("Item updated successfully!");
      setSnackbarOpen(true);
      setEditModalOpen(false);
      setSelectedItem(null);

      // Clear the updatedValues cache for this item
      setUpdatedValues(prev => {
        const newValues = { ...prev };
        delete newValues[selectedItem.id];
        return newValues;
      });
    } catch (error) {
      console.error("Error updating item: ", error);
      setSnackbarMessage(`Error updating item: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      setSaveLoading(false);
    }
  };

  // Summary cards data
  const summaryCards = [
    {
      title: "Total Items",
      value: products.length,
      icon: <Inventory2OutlinedIcon />,
      color: theme.palette.primary.main,
    },
    {
      title: "In Stock",
      value: totals.totalQuantity,
      icon: <Inventory2OutlinedIcon />,
      color: theme.palette.success.main,
    },
    {
      title: "Stock Value",
      value: `£${totals.totalPrice.toFixed(2)}`,
      icon: <AttachMoneyOutlinedIcon />,
      color: theme.palette.warning.main,
    },
    {
      title: "Categories",
      value: categories.length,
      icon: <LocalOfferOutlinedIcon />,
      color: theme.palette.info.main,
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8f9fa", p: 3 }}>
      {/* ================= HEADER ================= */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
            Inventory Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all inventory items and stock levels
          </Typography>
        </Box>

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<LocalOfferOutlinedIcon />}
            onClick={() => setAddCategoryOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            Add Category
          </Button>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={() => setAddProductOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {/* ================= SUMMARY CARDS ================= */}
      <Grid container spacing={3} mb={4}>
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 2,
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      {card.title}
                    </Typography>
                    <Typography variant="h5" fontWeight={700} mt={1}>
                      {card.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: `${card.color}15`,
                      p: 1.5,
                      borderRadius: 2,
                    }}
                  >
                    {React.cloneElement(card.icon, { sx: { color: card.color, fontSize: 28 } })}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ================= FILTER BAR ================= */}
      <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 4, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              placeholder="Search by item name or category..."
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              select
              fullWidth
              label="Filter by Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              InputProps={{
                startAdornment: <FilterListIcon sx={{ mr: 1, color: "text.secondary" }} />,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.category}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box display="flex" gap={1}>
              <Button
                variant={viewMode === "grid" ? "contained" : "outlined"}
                onClick={() => setViewMode("grid")}
                fullWidth
              >
                Grid
              </Button>
              <Button
                variant={viewMode === "table" ? "contained" : "outlined"}
                onClick={() => setViewMode("table")}
                fullWidth
              >
                Table
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>

      {/* ================= CONTENT AREA ================= */}
      {initialLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          {/* ================= GRID VIEW ================= */}
          {viewMode === "grid" && (
            <Grid container spacing={3}>
              {paginatedProducts.map((product) => {
                const stockStatus = getStockStatus(product.availableQuantity);
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                    <Card
                      sx={{
                        height: "100%",
                        borderRadius: 3,
                        boxShadow: 2,
                        transition: "all 0.3s",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: 6,
                        },
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <CardContent>
                        {/* Header with title and actions */}
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Box>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                              {product.title}
                            </Typography>
                            <Chip
                              label={getCategoryName(product.categoryId)}
                              size="small"
                              sx={{
                                backgroundColor: `${theme.palette.primary.light}20`,
                                color: theme.palette.primary.dark,
                                fontWeight: 500,
                              }}
                            />
                          </Box>
                          <Box display="flex" gap={0.5}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedItem(product);
                                setEditModalOpen(true);
                              }}
                              sx={{ color: "primary.main" }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setItemToDelete(product);
                                setDeleteConfirmOpen(true);
                              }}
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        {/* Product details */}
                        <Box mt={2}>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Brand
                              </Typography>
                              <Typography fontWeight={500}>
                                {product.brand || "N/A"}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Vendor
                              </Typography>
                              <Typography fontWeight={500}>
                                {product.vendor || "N/A"}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Units
                              </Typography>
                              <Typography fontWeight={500}>
                                {product.units || "N/A"}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Available Qty
                              </Typography>
                              <Typography fontWeight={600} color={
                                stockStatus === "out"
                                  ? "error.main"
                                  : stockStatus === "low"
                                    ? "warning.main"
                                    : "success.main"
                              }>
                                {product.availableQuantity}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Price/Unit
                              </Typography>
                              <Typography fontWeight={600} color="primary.main">
                                £ {parseFloat(product.price || 0).toFixed(2)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Stock Value
                              </Typography>
                              <Typography fontWeight={600} color="warning.main">
                                £ {(parseFloat(product.price || 0) * parseFloat(product.availableQuantity || 0)).toFixed(2)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Sold Qty
                              </Typography>
                              <Typography fontWeight={500} color="error.main">
                                {product.soldQuantity || 0}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">
                                Updated
                              </Typography>
                              <Typography variant="caption">
                                {product.updatedAt
                                  ? new Date(product.updatedAt.seconds * 1000).toLocaleDateString()
                                  : "N/A"}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
          )}

          {/* ================= TABLE VIEW ================= */}
          {viewMode === "table" && (
            <Card sx={{ borderRadius: 3, boxShadow: 2, overflow: "hidden" }}>
              <Box sx={{
                overflowX: "auto", "& table": {
                  minWidth: "1400px" // Adjust this based on your needs
                }
              }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: theme.palette.primary.main }}>
                      <th style={{ padding: "16px", textAlign: "left", color: "white" }}>
                        <Typography fontWeight={600}>Title</Typography>
                      </th>
                      <th style={{ padding: "10px", textAlign: "left", color: "white" }}>
                        <Typography fontWeight={600}>Brand</Typography>
                      </th>
                      <th style={{ padding: "10px", textAlign: "left", color: "white" }}>
                        <Typography fontWeight={600}>Category</Typography>
                      </th>
                      <th style={{ padding: "16px", textAlign: "left", color: "white" }}>
                        <Typography fontWeight={600}>Units</Typography>
                      </th>
                      <th style={{ padding: "10px", textAlign: "left", color: "white" }}>
                        <Typography fontWeight={600}>Available Qty</Typography>
                      </th>
                      <th style={{ padding: "10px", textAlign: "left", color: "white" }}>
                        <Typography fontWeight={600}>Price/Unit</Typography>
                      </th>
                      <th style={{ padding: "10px", textAlign: "left", color: "white" }}>
                        <Typography fontWeight={600}>Sold Qty</Typography>
                      </th>
                      <th style={{ padding: "10px", textAlign: "left", color: "white" }}>
                        <Typography fontWeight={600}>Total Price</Typography>
                      </th>
                      <th style={{ padding: "10px", textAlign: "left", color: "white" }}>
                        <Typography fontWeight={600}>Updated At</Typography>
                      </th>
                      <th style={{ padding: "10px", textAlign: "left", color: "white" }}>
                        <Typography fontWeight={600}>Actions</Typography>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProducts.map((product) => {
                      const stockStatus = getStockStatus(product.availableQuantity);
                      return(
                      <tr
                        key={product.id}
                        style={{
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          backgroundColor: "white",
                          "&:hover": { backgroundColor: theme.palette.action.hover }
                        }}
                      >
                        {/* Title */}
                        <td style={{ padding: "16px" }}>
                          <Typography fontWeight={500}>{product.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {product.vendor || "N/A"}
                          </Typography>
                        </td>

                        {/* Brand */}
                        <td style={{ padding: "10px" }}>
                          <Typography>{product.brand || "N/A"}</Typography>
                        </td>

                        {/* Category */}
                        <td style={{ padding: "10px" }}>
                          <Chip
                            label={getCategoryName(product.categoryId)}
                            size="small"
                            sx={{
                              backgroundColor: `${theme.palette.primary.light}20`,
                              color: theme.palette.primary.dark,
                            }}
                          />
                        </td>

                        {/* Units */}
                        <td style={{ padding: "10px" }}>
                          <Typography>{product.units || "N/A"}</Typography>
                        </td>

                        {/* Available Quantity */}
                        <td style={{ padding: "10px" }}>
                          <Box>
                            <Typography
                              fontWeight={600}
                              color={
                                stockStatus === "out"
                                  ? "error.main"
                                  : stockStatus === "low"
                                    ? "warning.main"
                                    : "success.main"
                              }
                            >
                              {product.availableQuantity}
                            </Typography>

                            {stockStatus !== "ok" && (
                              <Typography variant="caption" color="error">
                                {stockStatus === "out" ? "Out of stock" : "Low stock"}
                              </Typography>
                            )}
                          </Box>
                        </td>

                        {/* Price per Unit */}
                        <td style={{ padding: "10px" }}>
                          <Typography fontWeight={500} color="primary.main">
                            £ {parseFloat(product.price || 0).toFixed(2)}
                          </Typography>
                        </td>

                        {/* Sold Quantity */}
                        <td style={{ padding: "10px" }}>
                          <Typography fontWeight={500} color="error.main">
                            {product.soldQuantity || 0}
                          </Typography>
                        </td>

                        {/* Total Price */}
                        <td style={{ padding: "16px" }}>
                          <Typography fontWeight={600} color="warning.main">
                            £ {(parseFloat(product.price || 0) * parseFloat(product.availableQuantity || 0)).toFixed(2)}
                          </Typography>
                        </td>

                        {/* Updated At */}
                        <td style={{ padding: "10px" }}>
                          <Typography variant="body2">
                            {product.updatedAt
                              ? new Date(product.updatedAt.seconds * 1000).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })
                              : product.createdAt
                                ? new Date(product.createdAt).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })
                                : "N/A"}
                          </Typography>
                          {product.updatedAt && (
                            <Typography variant="caption" color="text.secondary">
                              {new Date(product.updatedAt.seconds * 1000).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                          )}
                        </td>

                        {/* Actions */}
                        <td style={{ padding: "10px" }}>
                          <Box display="flex" gap={1}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedItem(product);
                                setEditModalOpen(true);
                              }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setItemToDelete(product);
                                setDeleteConfirmOpen(true);
                              }}
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </td>
                      </tr>
                    )
                    })}
                  </tbody>
                </table>
              </Box>
            </Card>
          )}

          {/* ================= PAGINATION ================= */}
          {filteredProducts.length > itemsPerPage && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={Math.ceil(filteredProducts.length / itemsPerPage)}
                page={currentPage}
                onChange={(_, value) => setCurrentPage(value)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: 2,
                    mx: 0.5,
                  },
                }}
              />
            </Box>
          )}

          {/* ================= TOTALS SUMMARY ================= */}
          {viewMode === "table" && (
            <Card sx={{ mt: 3, borderRadius: 3, boxShadow: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Items
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {filteredProducts.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Stock Value
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="warning.main">
                      £ {totals.totalPrice.toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total In Stock
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="success.main">
                      {totals.totalQuantity}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Sold
                    </Typography>
                    <Typography variant="h6" fontWeight={600} color="error.main">
                      {totals.totalSold}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ================= MODALS ================= */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{itemToDelete?.title}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <AddCategoryModal
        open={addCategoryOpen}
        onClose={() => setAddCategoryOpen(false)}
      />
      <AddProductModal
        open={addProductOpen}
        onClose={() => setAddProductOpen(false)}
      />

      <EditItemModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedItem(null);
        }}
        item={selectedItem}
        categories={categories}
        onSave={handleSaveEdit}
        loading={saveLoading}
      />

      {/* ================= SNACKBAR ================= */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default Inventory;